// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Charts
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import {
  Clock,
  CalendarDays,
  Ban,
  CircleCheckBig,
  FileCheckCorner,
  Truck,
  Undo2,
  HandCoins,
  Wallet,
  TrendingUp,
  Users,
  DollarSign,
} from "lucide-react";
// Cards
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Icons
import { ShoppingCart, AutoMode } from "@mui/icons-material";

// Utils
import { SkPrice } from "Utils/dateFunc";

// React
import { useEffect, useState } from "react";
import axios from "axios";

function Dashboard() {
  const admin = localStorage.getItem("admin_id");

  const [isDashboard, setIsDashboard] = useState(null);
  const [serviceStats, setServiceStats] = useState({});
  const [ecommStats, setEcommStats] = useState({});
  const [dashboardStats, setDashboardStats] = useState({});

  const [monthChart, setMonthChart] = useState({});
  const [dayChart, setDayChart] = useState({});
  const [userChart, setUserChart] = useState({});

  // ================= FETCH DASHBOARD =================
  useEffect(() => {
    (async () => {
      try {
        // Fetch main dashboard data
        const res = await axios.get(`${process.env.REACT_APP_APII}/dashBoard/${admin}`, {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const data = res?.data?.data;
        setIsDashboard(data);

        // Stats
        setServiceStats(data?.service?.counts || {});
        setEcommStats(data?.ecommerce?.counts || {});

        // Fetch dashboard stats
        const statsRes = await axios.get(`${process.env.REACT_APP_APII}/dashboard-stats`, {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (statsRes.data.success) {
          setDashboardStats(statsRes.data.data);
        }
      } catch (err) {
        
      }
    })();
  }, [admin]);

  // ================= CHART DATA =================
  useEffect(() => {
    if (!isDashboard) return;

    // Ecommerce monthly orders
    setMonthChart({
      labels: Object.keys(isDashboard?.ecommerce?.graphs?.monthlyOrders || {}),
      datasets: {
        label: "Orders",
        data: Object.values(isDashboard?.ecommerce?.graphs?.monthlyOrders || {}),
      },
    });

    // Ecommerce weekday orders
    setDayChart({
      labels: Object.keys(isDashboard?.ecommerce?.graphs?.weekdayOrders || {}),
      datasets: {
        label: "Orders",
        data: Object.values(isDashboard?.ecommerce?.graphs?.weekdayOrders || {}),
      },
    });

    // Monthly users
    setUserChart({
      labels: Object.keys(isDashboard?.commonGraphs?.monthlyUsers || {}),
      datasets: {
        label: "Users",
        data: Object.values(isDashboard?.commonGraphs?.monthlyUsers || {}),
      },
    });
  }, [isDashboard]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        {/* ================= TOP STATS ================= */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ComplexStatisticsCard
              color="error"
              icon="group_add"
              title="Total Users"
              count={isDashboard?.users?.total || 0}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <ComplexStatisticsCard
              icon={<ShoppingCart />}
              title="Total Orders"
              count={isDashboard?.total || 0}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <ComplexStatisticsCard
              color="success"
              icon={<AutoMode />}
              title="Turnover"
              count={SkPrice(isDashboard?.totalTurnover || 0)}
            />
          </Grid>
        </Grid>

        {/* ================= FINANCIAL OVERVIEW ================= */}
        <MDBox mt={5}>
          <MDTypography variant="h6" mb={3}>Financial Overview</MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <ComplexStatisticsCard
                color="success"
                icon={<DollarSign />}
                title="Total Earnings"
                count={SkPrice(dashboardStats?.totalEarning || 0)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ComplexStatisticsCard
                color="info"
                icon={<Wallet />}
                title="Total Withdrawn"
                count={SkPrice(dashboardStats?.totalWithdrawn || 0)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ComplexStatisticsCard
                color="warning"
                icon={<TrendingUp />}
                title="Available Balance"
                count={SkPrice(dashboardStats?.totalAvailable || 0)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ComplexStatisticsCard
                color="error"
                icon={<Users />}
                title="Active Partners"
                count={dashboardStats?.activePartners || 0}
              />
            </Grid>
          </Grid>
          
          {/* Pending Withdrawals Info */}
          {(dashboardStats?.pendingWithdrawalsCount > 0 || dashboardStats?.pendingWithdrawalsAmount > 0) && (
            <Grid container spacing={3} mt={3}>
              <Grid item xs={12} md={6}>
                <ComplexStatisticsCard
                  color="warning"
                  icon={<Clock />}
                  title="Pending Withdrawals"
                  count={dashboardStats?.pendingWithdrawalsCount || 0}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ComplexStatisticsCard
                  color="error"
                  icon={<HandCoins />}
                  title="Pending Amount"
                  count={SkPrice(dashboardStats?.pendingWithdrawalsAmount || 0)}
                />
              </Grid>
            </Grid>
          )}
        </MDBox>

        {/* ================= ORDER CHARTS ================= */}
        <MDBox mt={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ReportsBarChart
                color="info"
                title="Monthly Orders"
                date="Updated"
                chart={monthChart}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ReportsLineChart
                color="dark"
                title="Weekly Orders"
                date="Updated"
                chart={dayChart}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* ================= SERVICE STATS ================= */}
        <MDBox mt={5}>
          <MDTypography variant="h6">Service Orders</MDTypography>
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="warning"
                icon={<Clock />}
                title="Pending"
                count={serviceStats?.PENDING_BOOKING || 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="primary"
                icon={<CalendarDays />}
                title="Upcoming"
                count={serviceStats?.UPCOMING_BOOKING || 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="success"
                icon={<CircleCheckBig />}
                title="Completed"
                count={serviceStats?.COMPLETED_BOOKING || 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="error"
                icon={<Ban />}
                title="Cancelled"
                count={serviceStats?.CANCELLED_BOOKING || 0}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* ================= USER CHART ================= */}
        <MDBox mt={5}>
          <ReportsBarChart color="info" title="User Growth" date="Updated" chart={userChart} />
        </MDBox>

        {/* ================= ECOMMERCE STATS ================= */}
        <MDBox mt={5}>
          <MDTypography variant="h6">Ecommerce Orders</MDTypography>
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="primary"
                icon={<FileCheckCorner />}
                title="Ordered"
                count={ecommStats?.ORDERED_ORDER || 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="success"
                icon={<Truck />}
                title="Delivered"
                count={ecommStats?.DELIVERED_ORDER || 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="error"
                icon={<Undo2 />}
                title="Returned"
                count={ecommStats?.RETURNED_ORDER || 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ComplexStatisticsCard
                color="error"
                icon={<HandCoins />}
                title="Refunded"
                count={ecommStats?.REFUNDED_ORDER || 0}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
